import * as React from "react";
import { Info, Inbox, AlertTriangle, RefreshCw } from "lucide-react";

/**
 * EmptyState — every list/pane should have one, in the product's sober voice.
 *
 * <EmptyState icon={CalendarClock} title="No bookings today" body="Add one from the
 *   toolbar or import." action={<button>Add booking</button>} />
 */
export function EmptyState({
  icon: Icon = Inbox,
  eyebrow = "Nothing here yet",
  title = "",
  body = null,
  action = null,
  className = "",
  testid = "empty-state",
}) {
  return (
    <div
      className={`warm-empty px-6 py-10 md:py-14 text-center ${className}`}
      data-testid={testid}
    >
      <div className="mx-auto h-11 w-11 rounded-full bg-card border border-brand-rule flex items-center justify-center text-brand-muted">
        <Icon size={18} />
      </div>
      <div className="mt-4 micro-label">{eyebrow}</div>
      {title && <div className="mt-1 text-[15px] font-semibold text-brand-ink">{title}</div>}
      {body && <div className="mt-1 text-[12.5px] text-brand-muted max-w-[420px] mx-auto leading-relaxed">{body}</div>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

/**
 * ErrorState — inline error surface for any fetch failure. Always show a
 * retry unless explicitly hidden. Never surface raw error strings — this
 * component's `body` is written by the caller in human copy.
 */
export function ErrorState({
  icon: Icon = AlertTriangle,
  title = "That didn't load",
  body = "The request failed. Try again in a moment.",
  onRetry = null,
  className = "",
  testid = "error-state",
}) {
  return (
    <div
      className={`rounded-lg border border-brand-coral/40 bg-brand-coral/5 p-4 flex items-start gap-3 ${className}`}
      role="alert"
      data-testid={testid}
    >
      <div className="h-8 w-8 rounded-full bg-card border border-brand-coral/50 flex items-center justify-center text-brand-coral shrink-0">
        <Icon size={14} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-brand-ink">{title}</div>
        <div className="mt-0.5 text-[12px] text-brand-muted leading-relaxed">{body}</div>
      </div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="btn-outline shrink-0"
          data-testid={`${testid}-retry`}
        >
          <RefreshCw size={12} /> Retry
        </button>
      )}
    </div>
  );
}

export function InlineInfo({ body, className = "" }) {
  return (
    <div className={`flex items-start gap-2 text-[12px] text-brand-muted ${className}`}>
      <Info size={13} className="mt-0.5 shrink-0" />
      <div>{body}</div>
    </div>
  );
}
